import { ErrorBoundary, Shadow, Text, HStack, Stack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import React from 'react';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { CustomerDetailStackParamList } from '../../../../../../routes/types';
import { customerDetailPortfolioProfitabilityQuery } from '../../../../../../store/customerDetail/selector';

export const PortfolioProfitability: React.FC = () => {
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const { data, error, loading, refresher } = useRecoilQuery(
    customerDetailPortfolioProfitabilityQuery({ accountId: route.params.accountId })
  );

  if (error) {
    return (
      <Shadow level={1} width="full" padding={Spacing['inset.xs']} borderRadius={BoxRadius.sm}>
        <ErrorBoundary onRefresh={refresher} />
      </Shadow>
    );
  }

  return (
    <Shadow level={1} width="full" padding={Spacing['inset.xs']} borderRadius={BoxRadius.sm}>
      <Stack
        width="full"
        direction={{ base: 'column', lg: 'row' }}
        space={Spacing['stack.nano']}
        alignItems={{ base: 'stretch', lg: 'center' }}>
        <Text
          flex={1}
          isLoading={loading}
          variant="bold"
          fontSize={{ base: Typography['fontsize.md'], lg: Typography['fontsize.lg'] }}>
          Rentabilidade da Carteira
        </Text>
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          space={{ base: Spacing['stack.nano'], lg: Spacing['stack.xxs'] }}>
          <Stack
            direction={{ base: 'row', lg: 'column' }}
            justifyContent={{ base: 'space-between' }}
            space={{ lg: Spacing['stack.quarck'] }}>
            <Text isLoading={loading} variant="bold">
              Ano
            </Text>
            <HStack space={Spacing['stack.quarck']} minWidth="100px">
              <Text isLoading={loading}>{data?.yearValueNormalized}</Text>
              <Text
                isLoading={loading}
                color={
                  data?.yearValuePercentageColorNormalized ?? Utils.ColorToken.NUMBER_NEUTRAL_COLOR
                }>
                {data?.yearValuePercentageNormalized}
              </Text>
            </HStack>
          </Stack>
          <Stack
            direction={{ base: 'row', lg: 'column' }}
            justifyContent={{ base: 'space-between' }}
            space={{ lg: Spacing['stack.quarck'] }}>
            <Text isLoading={loading} variant="bold">
              Mês
            </Text>
            <HStack space={Spacing['stack.quarck']} minWidth="100px">
              <Text isLoading={loading}>{data?.monthValueNormalized}</Text>
              <Text
                isLoading={loading}
                color={
                  data?.monthValuePercentageColorNormalized ?? Utils.ColorToken.NUMBER_NEUTRAL_COLOR
                }>
                {data?.monthValuePercentageNormalized}
              </Text>
            </HStack>
          </Stack>
          <Stack
            direction={{ base: 'row', lg: 'column' }}
            justifyContent={{ base: 'space-between' }}
            space={{ lg: Spacing['stack.quarck'] }}>
            <Text isLoading={loading} variant="bold">
              12 meses
            </Text>
            <HStack space={Spacing['stack.quarck']} minWidth="100px">
              <Text isLoading={loading}>{data?.twelveMonthsValueNormalized}</Text>
              <Text
                isLoading={loading}
                color={
                  data?.twelveMonthsValuePercentageColorNormalized ??
                  Utils.ColorToken.NUMBER_NEUTRAL_COLOR
                }>
                {data?.twelveMonthsValuePercentageNormalized}
              </Text>
            </HStack>
          </Stack>
          <Stack
            direction={{ base: 'row', lg: 'column' }}
            justifyContent={{ base: 'space-between' }}
            space={{ lg: Spacing['stack.quarck'] }}>
            <Text isLoading={loading} variant="bold">
              Desde o início
            </Text>
            <HStack space={Spacing['stack.quarck']} minWidth="100px">
              <Text isLoading={loading}>{data?.allTimeValueNormalized}</Text>
              <Text
                isLoading={loading}
                color={
                  data?.allTimeValuePercentageColorNormalized ??
                  Utils.ColorToken.NUMBER_NEUTRAL_COLOR
                }>
                {data?.allTimeValuePercentageNormalized}
              </Text>
            </HStack>
          </Stack>
        </Stack>
      </Stack>
    </Shadow>
  );
};
